import {
  WEEKDAY_NAMES, clockLabel, isDefaultDay, parseClock, protectedRange,
  type DayHours, type DayRule, type ProtectedHours, type Weekday,
} from "../../btb/protected-hours";
import { RULE_STEPS } from "../../btb/constants";

/** Monday first, the order owners read a week in. */
export const EDITOR_WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 0];
export const SHORT_DAY: Record<Weekday, string> = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

const STEP = RULE_STEPS.protectedHoursStepMinutes;
const DAY_END = 1440;

/** "8am – 8pm". Not `hoursText`: that name in src/btb/constants means "12 hours". */
export function openHoursText(h: DayHours): string {
  return `${clockLabel(parseClock(h.open) ?? 0)} – ${clockLabel(parseClock(h.close) ?? DAY_END)}`;
}

/** The day's valid range in minutes, or null when it protects nothing. */
const rangeOf = (rule: DayRule) => protectedRange(rule);

/** "11am – 4pm", "before 11am" (from midnight), "after 4pm" (to midnight), "all day" (both). */
export function rangeText(rule: DayRule): string {
  const r = rangeOf(rule);
  if (!r) return "";
  if (r.from === 0 && r.to === DAY_END) return "all day";
  if (r.from === 0) return `before ${clockLabel(r.to)}`;
  if (r.to === DAY_END) return `after ${clockLabel(r.from)}`;
  return `${clockLabel(r.from)} – ${clockLabel(r.to)}`;
}

/** The same range tightened for sentences: "11am–4pm". */
const tightRange = (rule: DayRule) => rangeText(rule).replace(" – ", "–");

export function dayWords(rule: DayRule, hours: DayHours): { caption: string; result: string } {
  if (!rule.btb) return { caption: "BTB is off on this day", result: "No BTB blocks all day" };
  const r = rangeOf(rule);
  if (!r) return { caption: `BTB blocks any time, ${openHoursText(hours)}`, result: "No protected hours" };
  const before = r.from > 0, after = r.to < DAY_END;
  const result = before && after
    ? `Blocks only before ${clockLabel(r.from)} and after ${clockLabel(r.to)}`
    : before ? `Blocks only before ${clockLabel(r.from)}`
    : after ? `Blocks only after ${clockLabel(r.to)}`
    : "No BTB blocks all day";
  return { caption: `No BTB ${rangeText(rule)}`, result };
}

const sameRule = (a: DayRule, b: DayRule) => a.btb === b.btb && a.protectFrom === b.protectFrom && a.protectTo === b.protectTo;

function joinNames(names: string[]): string {
  return names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function summarizeProtectedHours(ph: ProtectedHours): string {
  if (EDITOR_WEEKDAYS.every((d) => isDefaultDay(ph[d]))) return "BTB blocks every day, any time the salon is open";
  const runs: Weekday[][] = [];
  for (const d of EDITOR_WEEKDAYS) {
    if (!ph[d].btb) continue;
    const last = runs[runs.length - 1];
    const prev = last?.[last.length - 1];
    if (prev !== undefined && EDITOR_WEEKDAYS.indexOf(prev) + 1 === EDITOR_WEEKDAYS.indexOf(d) && sameRule(ph[prev], ph[d])) last.push(d);
    else runs.push([d]);
  }
  const segments = runs.map((run) => {
    const days = run.length === 1 ? SHORT_DAY[run[0]] : `${SHORT_DAY[run[0]]}–${SHORT_DAY[run[run.length - 1]]}`;
    const range = tightRange(ph[run[0]]);
    return `${days}: ${range ? `no BTB blocks ${range}` : "any time"}`;
  });
  const off = EDITOR_WEEKDAYS.filter((d) => !ph[d].btb).map((d) => SHORT_DAY[d]);
  if (off.length) segments.push(`Off ${joinNames(off)}`);
  return segments.join(" · ");
}

const hoursRange = (hours: DayHours) => {
  const open = parseClock(hours.open) ?? 0;
  const close = parseClock(hours.close) ?? DAY_END;
  return { open, span: Math.max(close - open, 1) };
};

/** A clock time's position along `hours`, 0 at opening and 1 at closing, clamped. */
export function railFraction(hhmm: string, hours: DayHours): number {
  const { open, span } = hoursRange(hours);
  const m = parseClock(hhmm) ?? open;
  return Math.min(1, Math.max(0, (m - open) / span));
}

const toClock = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** Snaps a fraction to the nearest half hour inside the rail; only the exact ends mean midnight. */
export function clockAtFraction(f: number, hours: DayHours): string {
  if (f <= 0) return "00:00";
  if (f >= 1) return "24:00";
  const { open, span } = hoursRange(hours);
  const snapped = Math.round((open + f * span) / STEP) * STEP;
  return toClock(Math.min(DAY_END, Math.max(0, snapped)));
}

/** 12:00–14:00 clamped into the open hours, which a short day can shrink. */
export function defaultRangeFor(hours: DayHours): { protectFrom: string; protectTo: string } {
  const open = parseClock(hours.open) ?? 0;
  const close = parseClock(hours.close) ?? DAY_END;
  const from = Math.max(open, Math.min(12 * 60, close - 120));
  const to = Math.min(close, from + 120);
  return { protectFrom: toClock(from), protectTo: toClock(to) };
}

const dayState = (rule: DayRule) => {
  if (!rule.btb) return "off";
  const range = tightRange(rule);
  return range ? `no BTB blocks ${range}` : "BTB blocks any time";
};

/** One line per weekday whose rule changed, Monday first. */
export function diffProtectedHours(before: ProtectedHours, after: ProtectedHours): string[] {
  return EDITOR_WEEKDAYS
    .filter((d) => dayState(before[d]) !== dayState(after[d]))
    .map((d) => `${WEEKDAY_NAMES[d]}: ${dayState(before[d])} → ${dayState(after[d])}`);
}

/** Save-dialog warning when a change can remove blocks already placed; null otherwise. */
export function protectedChangeSentence(before: ProtectedHours, after: ProtectedHours): string | null {
  const removes = EDITOR_WEEKDAYS.some((d) => {
    const a = before[d], b = after[d];
    if (!a.btb) return false;
    if (!b.btb) return true;
    const nb = rangeOf(b);
    if (!nb) return false;
    const na = rangeOf(a);
    return !na || nb.from < na.from || nb.to > na.to;
  });
  return removes ? "BTB blocks this system already placed inside the new protected hours, or on days now switched off, will be removed on the next pass." : null;
}
